import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Feedback, FeedbackRequest } from '../models/feedback.model';

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {
  private apiUrl = `${environment.apiUrl}/global_admin/feedback`;

  constructor(private http: HttpClient) {}

  getAllFeedback(page: number = 0, size: number = 10): Observable<PageResponse<Feedback>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageResponse<Feedback>>(this.apiUrl, { params });
  }

  getFeedbackById(id: string): Observable<Feedback> {
    return this.http.get<Feedback>(`${this.apiUrl}/${id}`);
  }

  getFeedbackByType(type: string, page: number = 0, size: number = 10): Observable<PageResponse<Feedback>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageResponse<Feedback>>(`${this.apiUrl}/type/${type}`, { params });
  }

  getFeedbackByStatus(status: string, page: number = 0, size: number = 10): Observable<PageResponse<Feedback>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageResponse<Feedback>>(`${this.apiUrl}/status/${status}`, { params });
  }

  getFeedbackByUser(userId: string, page: number = 0, size: number = 10): Observable<PageResponse<Feedback>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageResponse<Feedback>>(`${this.apiUrl}/user/${userId}`, { params });
  }

  getFeedbackByTeam(teamId: string, page: number = 0, size: number = 10): Observable<PageResponse<Feedback>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageResponse<Feedback>>(`${this.apiUrl}/team/${teamId}`, { params });
  }

  getNewFeedback(): Observable<Feedback[]> {
    return this.http.get<Feedback[]>(`${this.apiUrl}/new`);
  }

  createFeedback(feedback: FeedbackRequest): Observable<Feedback> {
    return this.http.post<Feedback>(this.apiUrl, feedback);
  }

  updateFeedback(id: string, feedback: Partial<Feedback>): Observable<Feedback> {
    return this.http.put<Feedback>(`${this.apiUrl}/${id}`, feedback);
  }

  updateStatus(id: string, status: string): Observable<Feedback> {
    return this.http.patch<Feedback>(`${this.apiUrl}/${id}/status`, { status });
  }

  updateAdminNotes(id: string, adminNotes: string): Observable<Feedback> {
    return this.http.patch<Feedback>(`${this.apiUrl}/${id}/admin-notes`, { adminNotes });
  }

  deleteFeedback(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}
